import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatCurrency } from '@/lib/utils';
import { getDealsNeedingAttention } from '../api';
import { AlertTriangle, Clock, TrendingUp, ExternalLink } from 'lucide-react';
import type { DealNeedingAttention } from '../types';

interface DealsNeedingAttentionCardProps {
  accountId: string;
  onNavigateToTab: (tab: string) => void;
}

export default function DealsNeedingAttentionCard({
  accountId,
  onNavigateToTab,
}: DealsNeedingAttentionCardProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['account-deals-needing-attention', accountId],
    queryFn: () => getDealsNeedingAttention(accountId),
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Deals Needing Attention
          </CardTitle>
          <CardDescription>Deals that require follow-up</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Deals Needing Attention
          </CardTitle>
          <CardDescription>Deals that require follow-up</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">
            Unable to load deals data
          </p>
        </CardContent>
      </Card>
    );
  }

  // If no deals need attention, show a positive message
  if (data.totalCount === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Deals Needing Attention
          </CardTitle>
          <CardDescription>Deals that require follow-up</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-green-600">
            <span className="text-sm font-medium">All deals on track!</span>
          </div>
          <button
            onClick={() => onNavigateToTab('opportunities')}
            className="text-xs text-primary hover:underline mt-2"
          >
            View all opportunities &rarr;
          </button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-500" />
              Deals Needing Attention
            </CardTitle>
            <CardDescription>
              {data.totalCount} deal{data.totalCount !== 1 ? 's' : ''} requiring follow-up
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="closing" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-3">
            <TabsTrigger value="closing" className="text-xs">
              Closing
              {data.closingSoon.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px] bg-orange-100 text-orange-700">
                  {data.closingSoon.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="stalled" className="text-xs">
              Stalled
              {data.stalled.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px] bg-yellow-100 text-yellow-700">
                  {data.stalled.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="priority" className="text-xs">
              Priority
              {data.highPriority.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px] bg-red-100 text-red-700">
                  {data.highPriority.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="closing" className="mt-0">
            {data.closingSoon.length > 0 ? (
              <DealList deals={data.closingSoon} type="closing" />
            ) : (
              <EmptyState message="No deals closing this week" />
            )}
          </TabsContent>

          <TabsContent value="stalled" className="mt-0">
            {data.stalled.length > 0 ? (
              <DealList deals={data.stalled} type="stalled" />
            ) : (
              <EmptyState message="No stalled deals" />
            )}
          </TabsContent>

          <TabsContent value="priority" className="mt-0">
            {data.highPriority.length > 0 ? (
              <DealList deals={data.highPriority} type="priority" />
            ) : (
              <EmptyState message="No high priority deals" />
            )}
          </TabsContent>
        </Tabs>

        <button
          onClick={() => onNavigateToTab('opportunities')}
          className="text-xs text-primary hover:underline mt-3 block"
        >
          View all opportunities &rarr;
        </button>
      </CardContent>
    </Card>
  );
}

interface DealListProps {
  deals: DealNeedingAttention[];
  type: 'closing' | 'stalled' | 'priority';
}

function DealList({ deals, type }: DealListProps) {
  return (
    <div className="space-y-2">
      {deals.map((deal) => (
        <DealItem key={deal.id} deal={deal} type={type} />
      ))}
    </div>
  );
}

interface DealItemProps {
  deal: DealNeedingAttention;
  type: 'closing' | 'stalled' | 'priority';
}

function DealItem({ deal, type }: DealItemProps) {
  return (
    <div className="flex items-start justify-between p-2 rounded-md bg-muted/50 hover:bg-muted transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          {type === 'closing' && <Clock className="h-3 w-3 text-orange-500 flex-shrink-0" />}
          {type === 'stalled' && <AlertTriangle className="h-3 w-3 text-yellow-500 flex-shrink-0" />}
          {type === 'priority' && <TrendingUp className="h-3 w-3 text-red-500 flex-shrink-0" />}
          <p className="text-sm font-medium truncate" title={deal.title}>
            {deal.title}
          </p>
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-muted-foreground">
            {formatCurrency(deal.value)}
          </span>
          <span className="text-xs text-muted-foreground">
            &bull; {deal.stageLabel}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1 ml-2">
        <UrgencyIndicator deal={deal} type={type} />
        {deal.hubspotUrl && (
          <a
            href={deal.hubspotUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-primary"
            title="Open in HubSpot"
          >
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    </div>
  );
}

interface UrgencyIndicatorProps {
  deal: DealNeedingAttention;
  type: 'closing' | 'stalled' | 'priority';
}

function UrgencyIndicator({ deal, type }: UrgencyIndicatorProps) {
  if (type === 'closing' && deal.daysUntilClose !== undefined) {
    const days = deal.daysUntilClose;
    return (
      <Badge
        variant="outline"
        className={`text-[10px] h-5 ${
          days <= 2
            ? 'bg-red-50 text-red-700 border-red-200'
            : 'bg-orange-50 text-orange-700 border-orange-200'
        }`}
      >
        {days}d
      </Badge>
    );
  }

  if (type === 'stalled' && deal.daysInStage !== undefined) {
    return (
      <Badge
        variant="outline"
        className="text-[10px] h-5 bg-yellow-50 text-yellow-700 border-yellow-200"
      >
        {deal.daysInStage}d stalled
      </Badge>
    );
  }

  if (type === 'priority') {
    return (
      <Badge
        variant="outline"
        className="text-[10px] h-5 bg-red-50 text-red-700 border-red-200"
      >
        High
      </Badge>
    );
  }

  return null;
}

function EmptyState({ message }: { message: string }) {
  return (
    <p className="text-xs text-muted-foreground text-center py-3">{message}</p>
  );
}
